import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  ArrowLeft,
  Send,
  User,
  Bot,
  Clock,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Settings,
  MoreVertical,
} from 'lucide-react';

// Hook para detectar tamanho da tela
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    width: 1024,
  });

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width <= 640,
        isTablet: width > 640 && width <= 1024,
        isDesktop: width > 1024,
        width,
      });
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  return screenSize;
};

// Styled Components
const ChatContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: calc(100vh - 120px);
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: calc(100vh - 100px);
    border-radius: 8px;
  }
`;

const ChatHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background-color: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex: 1;
  min-width: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: white;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 36px;
    height: 36px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  min-width: 0;
`;

const UserAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  object-fit: cover;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 40px;
    height: 40px;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  .name {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ticket-info {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatusBadge = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  ${({ status, theme }) => {
    switch (status) {
      case 'NOVO':
        return `
          background-color: #3B82F6;
          color: white;
        `;
      case 'EM_ANDAMENTO':
        return `
          background-color: #F59E0B;
          color: white;
        `;
      case 'RESOLVIDO':
        return `
          background-color: #10B981;
          color: white;
        `;
      default:
        return `
          background-color: ${theme.colors.surfaceLight};
          color: ${theme.colors.text.secondary};
        `;
    }
  }}

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.375rem 0.625rem;
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: white;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 36px;
    height: 36px;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: #f8fafc;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const MessageGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Message = styled.div<{ tipo: string }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  align-items: flex-start;

  ${({ tipo }) =>
    tipo === 'usuario' &&
    `
    flex-direction: row-reverse;
  `}

  &:last-child {
    margin-bottom: 0;
  }
`;

const MessageAvatar = styled.div<{ tipo: string }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${({ tipo, theme }) => {
    switch (tipo) {
      case 'usuario':
        return `
          background-color: ${theme.colors.primary};
          color: white;
        `;
      case 'sistema':
        return `
          background-color: #6B7280;
          color: white;
        `;
      case 'atendente':
        return `
          background-color: #059669;
          color: white;
        `;
      default:
        return `
          background-color: ${theme.colors.surfaceLight};
          color: ${theme.colors.text.secondary};
        `;
    }
  }}

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 28px;
    height: 28px;
  }
`;

const MessageContent = styled.div<{ tipo: string }>`
  max-width: 70%;
  min-width: 120px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 80%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-width: 85%;
    min-width: 100px;
  }
`;

const MessageBubble = styled.div<{ tipo: string }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.5;
  word-wrap: break-word;

  ${({ tipo, theme }) => {
    switch (tipo) {
      case 'usuario':
        return `
          background-color: ${theme.colors.primary};
          color: white;
          border-bottom-right-radius: 4px;
        `;
      case 'sistema':
        return `
          background-color: #E5E7EB;
          color: #374151;
          border-bottom-left-radius: 4px;
        `;
      case 'atendente':
        return `
          background-color: white;
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border.light};
          border-bottom-left-radius: 4px;
        `;
      default:
        return `
          background-color: white;
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border.light};
        `;
    }
  }}

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }
`;

const MessageMeta = styled.div<{ tipo: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.muted};

  ${({ tipo }) =>
    tipo === 'usuario' &&
    `
    justify-content: flex-end;
  `}
`;

const InputContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background-color: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const InputWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-end;
`;

const MessageInput = styled.textarea`
  flex: 1;
  min-height: 44px;
  max-height: 120px;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: inherit;
  resize: none;
  background-color: white;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 16px; /* Evita zoom em iOS */
  }
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryDark || '#1D4ED8'};
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

interface Mensagem {
  id: number;
  tipo: 'usuario' | 'sistema' | 'atendente';
  autor: string;
  conteudo: string;
  timestamp: string;
}

interface Chamado {
  id: string;
  codigo: string;
  descricao: string;
  categoria: string;
  usuario: {
    nome: string;
    email: string;
    avatar: string;
  };
  atendente: {
    nome: string;
    status: string;
  };
  status: string;
  prioridade: string;
  ultimaAtualizacao: string;
  criado: string;
  mensagens: Mensagem[];
}

interface ChatProps {
  chamado: Chamado;
  onBack: () => void;
}

export const Chat: React.FC<ChatProps> = ({ chamado, onBack }) => {
  const screenSize = useResponsive();
  const [newMessage, setNewMessage] = useState('');
  const [mensagens, setMensagens] = useState<Mensagem[]>(chamado.mensagens);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll para a última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'NOVO':
        return <AlertCircle size={16} />;
      case 'EM_ANDAMENTO':
        return <RefreshCw size={16} />;
      case 'RESOLVIDO':
        return <CheckCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'NOVO':
        return 'Novo';
      case 'EM_ANDAMENTO':
        return 'Em Andamento';
      case 'RESOLVIDO':
        return 'Resolvido';
      default:
        return status;
    }
  };

  const getMessageIcon = (tipo: string) => {
    switch (tipo) {
      case 'usuario':
        return <User size={16} />;
      case 'sistema':
        return <Bot size={16} />;
      case 'atendente':
        return <User size={16} />;
      default:
        return <User size={16} />;
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const novaMensagem: Mensagem = {
      id: mensagens.length + 1,
      tipo: 'atendente',
      autor: 'Atendente',
      conteudo: newMessage.trim(),
      timestamp: new Date().toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    };

    setMensagens([...mensagens, novaMensagem]);
    setNewMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <ChatContainer>
      {/* Header do Chat */}
      <ChatHeader>
        <HeaderLeft>
          <BackButton onClick={onBack} title="Voltar">
            <ArrowLeft size={20} />
          </BackButton>
          <UserInfo>
            <UserAvatar
              src={chamado.usuario.avatar}
              alt={chamado.usuario.nome}
            />
            <UserDetails>
              <div className="name">{chamado.usuario.nome}</div>
              <div className="ticket-info">
                {chamado.codigo} • {chamado.categoria}
              </div>
            </UserDetails>
          </UserInfo>
        </HeaderLeft>
        <HeaderRight>
          <StatusBadge status={chamado.status}>
            {getStatusIcon(chamado.status)}
            {getStatusLabel(chamado.status)}
          </StatusBadge>
          <ActionButton title="Configurações">
            <Settings size={18} />
          </ActionButton>
          <ActionButton title="Mais opções">
            <MoreVertical size={18} />
          </ActionButton>
        </HeaderRight>
      </ChatHeader>

      {/* Container das Mensagens */}
      <MessagesContainer>
        <MessageGroup>
          {mensagens.map((mensagem) => (
            <Message key={mensagem.id} tipo={mensagem.tipo}>
              <MessageAvatar tipo={mensagem.tipo}>
                {getMessageIcon(mensagem.tipo)}
              </MessageAvatar>
              <MessageContent tipo={mensagem.tipo}>
                <MessageBubble tipo={mensagem.tipo}>
                  {mensagem.conteudo}
                </MessageBubble>
                <MessageMeta tipo={mensagem.tipo}>
                  <span>{mensagem.autor}</span>
                  <Clock size={12} />
                  <span>{mensagem.timestamp}</span>
                </MessageMeta>
              </MessageContent>
            </Message>
          ))}
          <div ref={messagesEndRef} />
        </MessageGroup>
      </MessagesContainer>

      {/* Input de Nova Mensagem */}
      <InputContainer>
        <InputWrapper>
          <MessageInput
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem..."
            rows={1}
          />
          <SendButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            title="Enviar mensagem"
          >
            <Send size={18} />
          </SendButton>
        </InputWrapper>
      </InputContainer>
    </ChatContainer>
  );
};
